import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { connect } from 'react-redux';
import { injectIntl, intlShape, defineMessages } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';

// Actions
import { addAndApplyFilter, removeAndApplyFilter } from '../../../actions/filterActions';

// Components
import { SbSharedRefineMenuFacet } from '../../SbSharedRefineMenu/SbSharedRefineMenuFacet/SbSharedRefineMenuFacet';
import { SbSharedRefineMenuInputCheckbox } from '../../SbSharedRefineMenu/SbSharedRefineMenuInput/SbSharedRefineMenuInputCheckbox';

// Constants
import {
    DISPLAY_PREFERENCE_OPTIONS,
    SOLD,
    SHOW_SOLD,
    NET_PRICE,
    MEASUREMENTS,
    RETURNABLE,
} from '../sbSharedRefineMenuConstants';
import { savePreference } from '../../../actions/displayPreferenceActions';

const messages = defineMessages({
    title: {
        id: 'sb.SbSharedRefineMenu.filter.display.title',
        defaultMessage: 'Display',
    },
});

class SbSharedRefineMenuDisplayPrefsSelectComponent extends React.Component {
    findFilter(filterName) {
        const { searchBrowse } = this.props;
        const appliedFilters = get(searchBrowse, 'appliedFilters');

        return appliedFilters.filter(appliedFilter => appliedFilter.name === filterName);
    }

    getFilterUrlLabel(filter) {
        return get(filter, '[0].values[0].urlLabel');
    }

    render() {
        const { filters, user, intl, showMeasurements, initiallyExpanded } = this.props;
        const filterName = 'display';
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const title = intl.formatMessage(messages.title);

        const displayPreferenceFilters = filters
            .filter(
                ({ name, values }) => DISPLAY_PREFERENCE_OPTIONS.includes(name) && values.length
            )
            .sort((a, b) => {
                return (
                    DISPLAY_PREFERENCE_OPTIONS.indexOf(a.name) -
                    DISPLAY_PREFERENCE_OPTIONS.indexOf(b.name)
                );
            });

        return (
            <React.Fragment>
                <SbSharedRefineMenuFacet
                    key={filterName}
                    title={title}
                    initiallyExpanded={initiallyExpanded}
                >
                    {displayPreferenceFilters.map((filter, index) => {
                        // net price display preference is only for trade users
                        if (filter.name === NET_PRICE && !isVerifiedTrade) {
                            return null;
                        }

                        // returnable display preference is only for non-trade users
                        if (filter.name === RETURNABLE && isVerifiedTrade) {
                            return null;
                        }

                        const showSoldFilter = this.findFilter(SHOW_SOLD);
                        const forceExcludeSold =
                            !!showSoldFilter.length &&
                            this.getFilterUrlLabel(showSoldFilter) === 'false';
                        const appliedFilterValue = this.findFilter(filter.name);

                        const value = get(filter, 'values[0]');
                        let isChecked = !!(
                            appliedFilterValue.length && this.getFilterUrlLabel(appliedFilterValue)
                        );

                        // get measurements preference from localStorage
                        if (filter.name === MEASUREMENTS) {
                            isChecked = !!showMeasurements;
                        }

                        if (filter.name === SOLD) {
                            // if showSold=false, we want the Exclude Sold box to be checked
                            isChecked = forceExcludeSold;
                        }

                        return (
                            <SbSharedRefineMenuInputCheckbox
                                key={`${value.urlLabel}-${index}`}
                                value={value.urlLabel}
                                checked={isChecked}
                                seoLink={value.link}
                                onChange={checked => {
                                    const { name } = filter;

                                    if (name === SOLD && forceExcludeSold) {
                                        if (checked) {
                                            this.props.onDeselect(SHOW_SOLD, {
                                                displayName: SHOW_SOLD,
                                                urlLabel: 'true',
                                            });
                                        } else {
                                            this.props.onDeselect(SHOW_SOLD, {
                                                displayName: SHOW_SOLD,
                                                urlLabel: 'false',
                                            });
                                        }
                                    }

                                    if (checked) {
                                        this.props.onSelect(name, value);
                                    } else {
                                        this.props.onDeselect(name, value);
                                    }
                                }}
                                text={value.displayName}
                            />
                        );
                    })}
                </SbSharedRefineMenuFacet>
            </React.Fragment>
        );
    }
}

SbSharedRefineMenuDisplayPrefsSelectComponent.defaultProps = {
    initiallyExpanded: false,
};

SbSharedRefineMenuDisplayPrefsSelectComponent.propTypes = {
    intl: intlShape.isRequired,
    searchBrowse: PropTypes.object.isRequired,
    onSelect: PropTypes.func.isRequired,
    onDeselect: PropTypes.func.isRequired,
    filters: PropTypes.array.isRequired,
    user: PropTypes.object,
    showMeasurements: PropTypes.bool,
    RefineMenuFacet: PropTypes.func,
    initiallyExpanded: PropTypes.bool,
};

function mapStateToProps({ displayPreferences }) {
    return {
        showMeasurements: displayPreferences.showMeasurements,
    };
}

function mapDispatchToProps(dispatch) {
    return {
        onSelect(name, changedPreference) {
            dispatch(savePreference(name, changedPreference));
            dispatch(addAndApplyFilter(name, changedPreference));
        },
        onDeselect(name, changedPreference) {
            dispatch(savePreference(name));
            dispatch(removeAndApplyFilter(name, changedPreference));
        },
    };
}

SbSharedRefineMenuDisplayPrefsSelectComponent = injectIntl(
    connect(
        mapStateToProps,
        mapDispatchToProps
    )(SbSharedRefineMenuDisplayPrefsSelectComponent)
);

export const SbSharedRefineMenuDisplayPrefsSelect = createFragmentContainer(
    SbSharedRefineMenuDisplayPrefsSelectComponent,
    {
        searchBrowse: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_searchBrowse on SearchBrowseQueryConnection {
                appliedFilters {
                    name
                    values {
                        urlLabel
                    }
                }
            }
        `,
        filters: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_filters on SearchBrowseFilter
                @relay(plural: true) {
                name
                values(limit: 15) {
                    displayName
                    urlLabel
                    link
                }
            }
        `,
        user: graphql`
            fragment SbSharedRefineMenuDisplayPrefsSelect_user on User {
                isVerifiedTrade
            }
        `,
    }
);
